import { useState, useEffect } from "react";
import styled from "styled-components";
import { Modal, List, Popconfirm, Select, message, Button } from "antd";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { DeleteTwoTone, FolderOpenOutlined, ProfileOutlined, SwapOutlined } from "@ant-design/icons";
import { GrossImage } from "../../../api/postAPI";
import { MediaGroup } from "../../../api/mediaGroupAPI";
import {
  PostCase,
  assignToPostCase,
  unassignFromPostCase,
  postCaseDisplayName,
} from "../../../api/postCaseAPI";
import { useMediaMultiSelect } from "./mediaMultiSelect";
import MediaSelectionToolbar from "./MediaSelectionToolbar";
import MediaDragBadge from "./MediaDragBadge";
import MediaGroupAssignModal from "./MediaGroupAssignModal";
import CaseAssignModal from "./CaseAssignModal";
import { groupIdOf } from "../mediaGroup";

const UNASSIGN_VALUE = "__unassign__";

interface Props {
  open: boolean;
  orderImageList: GrossImage[];
  onOk: () => void;
  onCancel: () => void;
  onDragEnd: (result: any, selectedIds: number[]) => void;
  onDeleteImage: (grossPhotoId: number) => void;
  onDeleteMany: (ids: number[]) => void;
  onConvertToPhoto?: (ids: number[]) => void;
  cases?: PostCase[];
  onCaseAssigned?: () => void;
  /** 미디어 그룹 (optional — 없으면 그룹 지정 액션 미노출) */
  groups?: MediaGroup[];
  onAssignGroup?: (
    mediaIds: number[],
    targetGroupId: number | null,
    newGroupName?: string
  ) => Promise<void>;
}

const GrossImageEditModal = ({
  open,
  orderImageList,
  onOk,
  onCancel,
  onDragEnd,
  onDeleteImage,
  onDeleteMany,
  onConvertToPhoto,
  cases = [],
  onCaseAssigned,
  groups,
  onAssignGroup,
}: Props) => {
  const hasCases = cases.length > 0;
  const {
    selectedIds,
    clear,
    handleRowClick,
    handleRowMouseDown,
    rangeRowProps,
    scrollRef,
    setDraggingId,
    rowState,
  } = useMediaMultiSelect(
    open,
    orderImageList.map((item) => item.grossPhotoId)
  );
  const [pendingChanges, setPendingChanges] = useState<Record<number, string | number>>({});
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [caseModalOpen, setCaseModalOpen] = useState(false);

  // 선택한 항목들의 Case 선택값을 한꺼번에 바꾼다 (실제 배정은 기존과 동일하게 OK 시점)
  const applyCaseToSelected = (value: number | string) => {
    setPendingChanges((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = value;
      });
      return next;
    });
  };

  // 행에 현재 소속 그룹명을 함께 보여준다 (배정 결과 확인용)
  const groupNameOf = (image: any): string | null => {
    const gid = groupIdOf(image);
    if (gid == null) return null;
    return groups?.find((g) => g.mediaGroupId === gid)?.name ?? null;
  };

  useEffect(() => {
    if (open) {
      setPendingChanges({});
    }
  }, [open]);

  const handleCaseChange = (image: GrossImage, value: number | string) => {
    if (!image.grossPhotoId) return;
    setPendingChanges((prev) => ({
      ...prev,
      [image.grossPhotoId!]: value,
    }));
  };

  const handleOk = async () => {
    const assignIdsByCase: Record<number, number[]> = {};
    const unassignIds: number[] = [];
    const currentIds = new Set(orderImageList.map((img) => img.grossPhotoId));

    Object.entries(pendingChanges).forEach(([idStr, caseId]) => {
      const grossPhotoId = Number(idStr);
      if (!currentIds.has(grossPhotoId)) return;

      if (caseId === UNASSIGN_VALUE) {
        unassignIds.push(grossPhotoId);
      } else {
        const cId = Number(caseId);
        if (!assignIdsByCase[cId]) assignIdsByCase[cId] = [];
        assignIdsByCase[cId].push(grossPhotoId);
      }
    });

    let isChanged = false;

    try {
      if (unassignIds.length > 0) {
        await unassignFromPostCase({ grossPhotoIds: unassignIds });
        isChanged = true;
      }

      for (const [cId, gIds] of Object.entries(assignIdsByCase)) {
        await assignToPostCase({
          postCaseId: Number(cId),
          grossPhotoIds: gIds,
        });
        isChanged = true;
      }

      if (isChanged) {
        message.success("Case 배정이 업데이트되었습니다.");
        onCaseAssigned?.();
      }
    } catch (error) {
      message.error("Case 배정 업데이트 중 오류가 발생했습니다.");
      console.error(error);
    }

    onOk();
  };

  return (
    <Modal
      title="Edit Gross Images"
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      centered
      width={680}
    >
      <MediaSelectionToolbar
        selectedCount={selectedIds.size}
        itemLabel="그로스 이미지"
        onDeleteSelected={() => {
          onDeleteMany(Array.from(selectedIds));
          clear();
        }}
        onClear={clear}
        extraActions={
          <>
            {hasCases && (
              <Button size="small" icon={<ProfileOutlined />} onClick={() => setCaseModalOpen(true)}>
                Case 배정
              </Button>
            )}
            {onAssignGroup && (
              <Button size="small" icon={<FolderOpenOutlined />} onClick={() => setGroupModalOpen(true)}>
                그룹 지정
              </Button>
            )}
            {onConvertToPhoto && (
              <Popconfirm
                title="Image로 이동"
                description={`선택한 ${selectedIds.size}개를 Image로 이동하시겠습니까?`}
                onConfirm={() => {
                  onConvertToPhoto(Array.from(selectedIds));
                  clear();
                }}
                okText="이동"
                cancelText="취소"
              >
                <Button size="small" icon={<SwapOutlined />}>
                  Image로 이동
                </Button>
              </Popconfirm>
            )}
          </>
        }
      />
      <ScrollBody ref={scrollRef}>
        <DragDropContext
          onDragStart={(start) =>
            setDraggingId(orderImageList[start.source.index]?.grossPhotoId ?? null)
          }
          onDragEnd={(r) => {
            setDraggingId(null);
            onDragEnd(r, Array.from(selectedIds));
          }}
        >
          <Droppable droppableId="viewerGrossImages">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <List
                  itemLayout="horizontal"
                  dataSource={orderImageList}
                  renderItem={(image, idx) => {
                    const rawCaseId = pendingChanges[image.grossPhotoId!] !== undefined 
                      ? pendingChanges[image.grossPhotoId!] 
                      : image.postCaseId;
                    
                    let selectValue = (rawCaseId === null || rawCaseId === undefined || rawCaseId === UNASSIGN_VALUE)
                      ? UNASSIGN_VALUE 
                      : Number(rawCaseId);

                    if (selectValue !== UNASSIGN_VALUE && !cases.some(c => c.postCaseId === selectValue)) {
                      selectValue = UNASSIGN_VALUE;
                    }

                    return (
                      <Draggable
                        key={`viewerGrossImage_${idx}`}
                        draggableId={`viewerGrossImage_${idx}`}
                        index={idx}
                      >
                        {(provided, snapshot) => {
                          const rs = rowState(image.grossPhotoId, snapshot.isDragging);
                          return (
                          <List.Item
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...rangeRowProps(idx)}
                            onMouseDown={(e) => handleRowMouseDown(e, idx)}
                            onClick={(e) => handleRowClick(e, image.grossPhotoId)}
                            style={{
                              ...(provided.draggableProps.style || {}),
                              background: rs.isSelected ? "#e6f4ff" : undefined,
                              opacity: rs.isGhosting ? 0.35 : undefined,
                              cursor: "pointer",
                            }}
                          >
                            {rs.showBadge && <MediaDragBadge count={rs.badgeCount} />}
                            <List.Item.Meta
                              avatar={
                                <div {...provided.dragHandleProps}>
                                  <img
                                    src={
                                      process.env.REACT_APP_BACKEND_URL +
                                      "/api/gross-photo/file/" +
                                      image?.pathFileName
                                    }
                                    alt={image?.caption}
                                    style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                  />
                                </div>
                              }
                              title={
                                <div {...provided.dragHandleProps}>
                                  <p style={{ margin: "0" }}>
                                    {image?.caption}
                                    {groupNameOf(image) && <GroupChip>{groupNameOf(image)}</GroupChip>}
                                  </p>
                                </div>
                              }
                            />
                            {hasCases && (
                              <Select
                                size="small"
                                style={{ width: 140, marginRight: 12 }}
                                value={selectValue}
                                onChange={(v) => handleCaseChange(image, v)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Select.Option value={UNASSIGN_VALUE}>미배정</Select.Option>
                                {cases.map((pc, i) => (
                                  <Select.Option key={pc.postCaseId} value={pc.postCaseId}>
                                    {pc.organ || postCaseDisplayName(pc, i)}
                                  </Select.Option>
                                ))}
                              </Select>
                            )}
                            <Popconfirm
                              title="Delete the gross image"
                              description="Are you sure to delete this gross image?"
                              onConfirm={() => image?.grossPhotoId && onDeleteImage(image.grossPhotoId)}
                              okText="Ok"
                              cancelText="Cancel"
                            >
                              <DeleteTwoTone
                                style={{
                                  marginLeft: "2rem",
                                  fontSize: "1.25rem",
                                  cursor: "pointer"
                                }}
                              />
                            </Popconfirm>
                          </List.Item>
                          );
                        }}
                      </Draggable>
                    );
                  }}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </ScrollBody>

      <MediaGroupAssignModal
        open={groupModalOpen}
        groups={groups ?? []}
        selectedCount={selectedIds.size}
        itemLabel="그로스 이미지"
        onCancel={() => setGroupModalOpen(false)}
        onSubmit={async ({ targetGroupId, newGroupName }) => {
          await onAssignGroup?.(Array.from(selectedIds), targetGroupId, newGroupName);
          setGroupModalOpen(false);
          clear();
        }}
      />

      <CaseAssignModal
        open={caseModalOpen}
        cases={cases}
        selectedCount={selectedIds.size}
        itemLabel="그로스 이미지"
        onCancel={() => setCaseModalOpen(false)}
        onSubmit={(value) => {
          applyCaseToSelected(value);
          setCaseModalOpen(false);
          clear();
        }}
      />
    </Modal>
  );
};

const GroupChip = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 8px;
  background: #f0f5ff;
  color: #2f54eb;
  font-size: 11px;
  line-height: 1.6;
  vertical-align: middle;
`;

const ScrollBody = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding: 16px 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #bfbfbf;
  }

  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 transparent;
`;

export default GrossImageEditModal;